import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { colors } from "constants/colors";
import { MarginStyleType } from "types/Common";
import ic_close from "assets/images/ic_close_normal_outlined_white_m.png";

export interface Props extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "style" | "color" | "onChange"
> {
  width?: string;
  color?: string;
  onChangeText?: (value: string) => void;
  textInputIsTyping?: (value: boolean) => void;
  inputFontSize?: number;
  borderColor?: string;
}

const TextInput = ({
  id,
  value,
  width,
  color,
  onChangeText,
  textInputIsTyping,
  inputFontSize = 16,
  borderColor = colors.lightGray1,
  maxLength,
  disabled = false,
  $marginTop,
  $marginLeft,
  $marginRight,
  $marginBottom,
  ...otherProps
}: Props & MarginStyleType) => {
  const [isTyping, setIsTyping] = useState(false);

  const fncOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setIsTyping(!!text);
      onChangeText && onChangeText(text);
    },
    [onChangeText],
  );

  return (
    <StyledContainer
      $marginTop={$marginTop}
      $marginLeft={$marginLeft}
      $marginRight={$marginRight}
      $marginBottom={$marginBottom}
      $widthValue={width}
      $isTyping={isTyping}
      $disabled={disabled}
      $borderColor={borderColor}
    >
      <StyledTextInput
        type="text"
        id={id}
        {...otherProps}
        $inputFontSize={inputFontSize}
        $color={color}
        $isTyping={isTyping}
        disabled={disabled}
        value={value}
        maxLength={maxLength}
        onChange={fncOnChange}
        onFocus={(e) => {
          setIsTyping(true);
          textInputIsTyping && textInputIsTyping(true);
          otherProps.onFocus && otherProps.onFocus(e);
        }}
        onBlur={(e) => {
          setIsTyping(false);
          textInputIsTyping && textInputIsTyping(false);
          otherProps.onBlur && otherProps.onBlur(e);
        }}
      />
      {isTyping && (
        <ClearButton
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            setIsTyping(false);
            onChangeText && onChangeText("");
          }}
        >
          <img src={ic_close} alt="" />
        </ClearButton>
      )}
    </StyledContainer>
  );
};
export default TextInput;

const StyledContainer = styled.div<
  MarginStyleType & {
    $widthValue?: string;
    $isTyping: boolean;
    $borderColor?: string;
    $disabled?: boolean;
  }
>`
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 44px;
  padding-right: 16px;
  border-radius: 12px;
  border: 1px solid
    ${({ $isTyping, $borderColor }) =>
      $isTyping ? colors.subPurple4 : $borderColor};
  background-color: ${({ $disabled }) =>
    $disabled ? colors.dimBg : colors.white};
  box-sizing: border-box;

  ${({ $widthValue }) =>
    $widthValue &&
    css`
      width: ${$widthValue};
    `};

  ${({ $marginTop }) =>
    $marginTop &&
    css`
      margin-top: ${$marginTop}px;
    `}
  ${({ $marginRight }) =>
    $marginRight &&
    css`
      margin-right: ${$marginRight}px;
    `}
  ${({ $marginLeft }) =>
    $marginLeft &&
    css`
      margin-left: ${$marginLeft}px;
    `}
  ${({ $marginBottom }) =>
    $marginBottom &&
    css`
      margin-bottom: ${$marginBottom}px;
    `}
`;

const StyledTextInput = styled.input<{
  $isTyping: boolean;
  $color?: string;
  $inputFontSize: number;
}>`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 8px 10px 16px;
  font-size: ${({ $inputFontSize }) => $inputFontSize}px;
  line-height: 24px;
  letter-spacing: -0.8px;
  color: ${({ $isTyping, $color, disabled }) =>
    $isTyping
      ? colors.mainPurple
      : disabled
        ? colors.gray
        : $color || colors.black};

  &::placeholder {
    color: ${colors.lightGray1};
  }
`;

const ClearButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: ${colors.gray};
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  font-size: 12px;
  img {
    width: 22px;
    height: 22px;
  }
`;
